/** The two fields the engine writes a currency as. */
export interface CurrencyFields {
  /** How much. */
  amount: number;
  /** The symbol. */
  currency: string;
}

/**
 * The currency value a rule produced, as a JavaScript value.
 *
 * The engine carries a currency as `{ amount, currency }` - the shape it also
 * READS as a currency, so a value that goes out and comes back is the same
 * value. What that shape does not carry across the boundary is how a currency
 * prints: `String(result)` on the plain object is `[object Object]`, which is
 * what a computed field displaying a price would have shown.
 *
 * So a returned currency arrives as this: the two fields it always had, plus
 * the two conversions - text with its symbol, and its amount in arithmetic -
 * that a JavaScript caller does implicitly.
 */
export class Currency {
  /** How much, as a number. */
  readonly amount: number;
  /** The symbol it was written with: `$`, `£`, `€`. */
  readonly currency: string;

  constructor(amount: number, currency: string) {
    this.amount = amount;
    this.currency = currency;
  }

  /** `$19.99` - the symbol against the amount, as the language writes it. */
  toString(): string {
    return `${this.currency}${this.amount}`;
  }

  /** The amount, so `price * 2` and `price > 10` mean what they read as. */
  valueOf(): number {
    return this.amount;
  }

  /** The same two fields, so `JSON.stringify` round-trips back to a currency. */
  toJSON(): CurrencyFields {
    return { amount: this.amount, currency: this.currency };
  }
}

/** A value as it arrives from the engine, before currencies are revived. */
type RawValue =
  | null
  | boolean
  | number
  | string
  | RawValue[]
  | { [key: string]: RawValue };

/**
 * Whether `value` is the shape the engine writes a currency as.
 *
 * The same test the engine applies on the way in - both fields present, the
 * amount a number or a numeric string, the symbol a non-empty string - so what
 * is revived here is exactly what would be read back as a currency there.
 */
function isEngineCurrency(
  value: Record<string, RawValue>,
): value is { amount: number | string; currency: string } {
  // The three checks below read representations on purpose: this is the
  // boundary where a JSON payload is parsed, and what arrives is whatever the
  // engine serialised.
  const amount = value['amount'];
  const currency = value['currency'];
  if (typeof currency !== 'string' || currency.length === 0) return false;
  if (typeof amount === 'number') return true;
  return (
    typeof amount === 'string' &&
    amount.trim() !== '' &&
    !Number.isNaN(Number(amount))
  );
}

/**
 * Revive every currency in a value the engine returned.
 *
 * Walks arrays and objects, because a rule may return a record of prices or a
 * list of them, and a currency two levels down is still a currency.
 */
export function reviveCurrencies(value: RawValue): RawValue | Currency {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    // SAFETY: an element is revived to itself, to a `Currency`, or to a copy
    // of the same structure - the array's own type is unchanged.
    return value.map((element) => reviveCurrencies(element)) as RawValue[];
  }
  if (isEngineCurrency(value)) {
    return new Currency(Number(value.amount), value.currency);
  }
  const revived: Record<string, RawValue> = {};
  for (const [key, member] of Object.entries(value)) {
    // SAFETY: as above - a revived member is the same data, with any currency
    // in it carrying its two conversions.
    revived[key] = reviveCurrencies(member) as RawValue;
  }
  return revived;
}
